import React, {useState, useRef, useImperativeHandle, useEffect, forwardRef, useContext, useCallback, useMemo} from 'react';
import { VideoContext } from '../context/VideoContext';
import { CuePoints } from '../data/Markers';
import {convertToSeconds} from '../utils/';
import gsap from 'gsap';

const VoiceOver = ({}) => {
  
  const voiceOverRef = useRef(null);
  const [audioHasLoaded, setAudioHasLoaded] = useState(false);
  const {currentOverlay, setCurrentOverlay, playAudio} = useContext(VideoContext);
  const [paused, setPaused] = useState(false);
  const tweenToNewCuepoint = useRef(null);
  const tweenEndCurrentClip = useRef(null);

  // handles play/pause & loading audio based on playAudio videoContext state
  useEffect(() => {
    
    const loadAudio = () => {
      if (audioHasLoaded) return;

      voiceOverRef.current.src = "./assets/voice-over.mp3";
      voiceOverRef.current.load();

      voiceOverRef.current.addEventListener('loadedmetadata', () => {
        setAudioHasLoaded(true);
      });
      voiceOverRef.current.onerror = () => {
        console.error('Error loading audio file.');
      };
    };
    
    // load audio?
    if (!audioHasLoaded && playAudio && !voiceOverRef.current.src) loadAudio();

    // fade audio out if it's playing
    if (!playAudio && audioHasLoaded) fadeOutAndPause();
    if (playAudio && audioHasLoaded) fadeInAndPlay();
    
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playAudio, audioHasLoaded, voiceOverRef]);

  const killTweens = useCallback(() => {
    tweenToNewCuepoint.current && tweenToNewCuepoint.current.kill();
    tweenEndCurrentClip.current && tweenEndCurrentClip.current.kill();
  }, []);

  // fadeInAndPlay, we just turned audio back on
  const fadeInAndPlay = useCallback((cuePoint) => {

    try {
      
      const cuePoint = CuePoints.find(c => c.Name === currentOverlay);
      
      const startTime = convertToSeconds(cuePoint.Start);
      const holdTime = convertToSeconds(cuePoint.Duration) + .5; // nudge time, for some reason durations are a little short
        
      gsap.killTweensOf(voiceOverRef.current);
        
      tweenToNewCuepoint.current = gsap.to(voiceOverRef.current, {
        volume: 0,
        duration: paused ? 0 : .35,
        onComplete: () => {
          voiceOverRef.current.volume = 1;
          try {
            voiceOverRef.current.currentTime = (startTime);
          } catch (error) {
            console.log(`error fastSeeking to startTime: ${error}`);
          }
          voiceOverRef.current.play();
        },
      });

      tweenEndCurrentClip.current = gsap.to(voiceOverRef.current, {
        volume: 0,
        duration: 0,
        delay: holdTime,
        onComplete: () => {
          setPaused(true);
          voiceOverRef.current.pause();
        }
      })

    } catch (error) {
      console.error(`\t fadeInAndPlay error parsing cuePoint = ${error}`);
    }
  }, [currentOverlay, playAudio, audioHasLoaded, killTweens, voiceOverRef]);

  // fadeOutAndPlay method seems to fail when we setCurrentOverlay to null
  const fadeOutAndPlay = useCallback((cuePoint) => {

    try {
      const startTime = convertToSeconds(cuePoint.Start);
      const holdTime = convertToSeconds(cuePoint.Duration) + .5; // nudge time, for some reason durations are a little short
      
      if (audioHasLoaded && playAudio) {
        
        gsap.killTweensOf(voiceOverRef.current);
        
        tweenToNewCuepoint.current = gsap.to(voiceOverRef.current, {
          volume: 0,
          duration: paused ? 0 : .35,
          onComplete: () => {
            voiceOverRef.current.volume = 1;
            try {
              voiceOverRef.current.currentTime = (startTime);
            } catch (error) {
              console.error(`error fastSeeking to startTime: ${error}`)
            }
            voiceOverRef.current.play();
          },
        });

        tweenEndCurrentClip.current = gsap.to(voiceOverRef.current, {
          volume: 0,
          duration: 0,
          delay: holdTime,
          onComplete: () => {
            setPaused(true);
            voiceOverRef.current.pause();
          }
        })
      }

    } catch (error) {
      console.error(`\t fadeOutAndPlay error parsing cuePoint = ${error}`);
    }
  }, [currentOverlay, playAudio, audioHasLoaded, killTweens, voiceOverRef]); // Add other dependencies if they exist

  const fadeOutAndPause = useCallback(() => {
   
    gsap.killTweensOf(voiceOverRef.current);

    tweenToNewCuepoint.current = gsap.to(voiceOverRef.current, {
      volume: 0,
      duration: .35,
      onComplete: () => {
        voiceOverRef.current.pause();
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [killTweens, voiceOverRef]);

  // determine which cuepoint to play based on the overlay that's currently active
  useEffect(() => {

    if (!playAudio || !audioHasLoaded) return;

    const cuePoint = CuePoints.find(c => c.Name === currentOverlay);
    
    gsap.killTweensOf(voiceOverRef.current);
    
    if (cuePoint) {
      fadeOutAndPlay(cuePoint);
    } else {
      tweenToNewCuepoint.current = gsap.to(voiceOverRef.current, {
        volume: 0,
        duration: .35,
        onComplete: () => {
          voiceOverRef.current.pause();
        }
      });
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOverlay]);

  const renderVoiceOver = useMemo(() => {
    return (
      <audio ref={voiceOverRef} controls className="">
        <source src=""></source>
      </audio>
    )
  }, []);
  
  return (
    <div id="AudioElement" className="fixed top-0 left-0 invisible">
      {renderVoiceOver}
    </div>
  );
};

export default VoiceOver;
